// Design tokens for the v2 rewrite.
//
// Single source of truth for the React layer AND the Tailwind config.
// Every value traces to a real-world material per .interface-design/system.md.
// If a key sounds generic (e.g. `surface-2`), it's wrong — it should sound
// like an object you could touch.

export const colors = {
  // Surfaces — asphalt at 1am, lifting through warm-beige stucco.
  // No drop shadows for elevation; each step is ~5% lightness up.
  asphalt: '#1a1612',
  stucco1: '#2a221c',
  stucco2: '#352b22',
  stucco3: '#3d3128',
  stucco4: '#4a3d31',
  edge: '#5a4a3c',
  void: '#130f0a',

  // Type
  bone: '#f5ecd6',
  marker: '#c8b89a',
  graphite: '#8a7e6c',
  sharpie: '#141008',

  // Stats — red/green readability rooted in materials
  aerosolRed: '#e6342a',
  glowStick: '#5fd17e',
  hotPink: '#ff2e7e',

  // Factions — both warm, fighting in the same family
  cholo: '#ff5a1f',
  emo: '#ff2e7e',

  // Tribes (Chicano LA universe — see docs/card-rename-mapping.md)
  slameros: '#e6342a',       // emo - mosh-pit aggression
  laTocada: '#ff2e7e',       // emo - the gig
  anclas: '#5fd17e',         // emo - backyard scene keepers
  plaqueros: '#c0c4ca',      // cholo - taggers
  laClica: '#ff8a3d',        // cholo - the crew
  bajitos: '#d4a945',        // cholo - lowriders

  // Ambient — the parking-lot lamp; replaces what cyan does in Hearthstone
  sodium: '#ff8a3d',
  sodiumGlow: '#ffb04a',
  candyPurple: '#6b2d8c',
  tecate: '#d4a945',

  // Freeze — the ONE cool intrusion, desaturated so it reads as foreign
  iceOverlay: '#9ec8d8',
  iceBorder: '#6ec1cc',
  iceCrystal: '#e8eef0',

  // Card preview surfaces (notebook-paper feel)
  parchment: '#f5e6c8',
  paperEdge: '#d9cfb5',
} as const;

// 4-unit grid; matches existing UIConstants.Spacing.
// Used wherever the v2 layer reaches for padding/margin/gap.
// Numeric keys (0..16) preserve Tailwind's default rem-scale so utilities
// like `top-1`, `left-2`, `gap-4` keep working alongside the named tokens.
export const spacing = {
  '0': '0px',
  px: '1px',
  '0.5': '2px',
  '1': '4px',
  '1.5': '6px',
  '2': '8px',
  '2.5': '10px',
  '3': '12px',
  '3.5': '14px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '28px',
  '8': '32px',
  '10': '40px',
  '12': '48px',
  '14': '56px',
  '16': '64px',
  '20': '80px',
  '24': '96px',
  // Named tokens (preferred for layout work; numerics for fine positioning)
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
  '4xl': '64px',
} as const;

// Semantic hairline border tokens — the bone-tinted edges used across every
// panel/card/modal in the v2 layer. Centralized so theme tweaks touch one
// file. Imported as raw strings (style={{ borderColor: EDGES.soft }}) instead
// of via Tailwind because most usages already pass through inline style.
export const EDGES = {
  faint: 'rgba(245,236,214,0.06)',
  soft: 'rgba(245,236,214,0.08)',
  mid: 'rgba(245,236,214,0.12)',
  primed: 'rgba(255,138,61,0.4)',
  primedSoft: 'rgba(255,138,61,0.25)',
} as const;

// Sharper than typical SaaS — the world is gritty.
export const radius = {
  sm: '2px', // buttons/inputs — almost square
  md: '4px', // cards/panels
  lg: '6px', // modals
} as const;

// Faction-aware typography carries the "vs."
// Old English on cholo surfaces; hand-marker on emo surfaces.
// JetBrains Mono for stats/timers (tabular numerals).
export const fontFamily = {
  cholo: ['"Pirata One"', '"UnifrakturCook"', '"Times New Roman"', 'serif'],
  emo: ['"Permanent Marker"', '"Special Elite"', '"Courier New"', 'monospace'],
  body: ['"Work Sans"', '"Inter"', 'system-ui', 'sans-serif'],
  data: ['"JetBrains Mono"', '"Roboto Mono"', '"Consolas"', 'monospace'],
} as const;

// Touch/legibility floors for the vertical-first layouts.
//
// The portrait pass found interactive controls as small as 26px tall and
// gameplay-critical labels (reroll/upgrade costs) at 7px. Both are below
// what a thumb can hit or an eye can read on a phone held at arm's length,
// and neither had any reason to be that small once the layout stopped
// fighting for width. Treat these as hard minimums for anything the player
// taps or must read mid-turn.
export const A11Y = {
  /** Minimum tap target, px. Matches the WCAG 2.2 / platform HIG floor. */
  minTapPx: 44,
  /** Minimum font size for text carrying meaning, px. */
  minTextPx: 10,
} as const;
